import { type UseFormProps, useController, useForm, useFormContext } from 'react-hook-form';

import type { SiteRedirectDestination, SiteRedirectSourcePath } from '@gitbook/api-client';
import { SITE_REDIRECT_SOURCE_PATH_PATTERN } from '@gitbook/api-client';
import {
    Description,
    FieldError,
    FieldGroup,
    Fieldset,
    Label,
    TextField,
    TextInput,
} from '@gitbook/spine-react';
import { FieldControllerProvider } from '@gitbook/spine-react/src/Form/FieldControllerContext';

import { useSiteContext } from '../../../../../state';
import {
    SiteRedirectDestinationPicker,
    type SiteRedirectDestinationPickerProps,
} from './SiteRedirectDestinationPicker';

export type SiteRedirectFormInputs = {
    source: string;
    destination: SiteRedirectDestination | null;
};

export type SiteRedirectFormOutputs = {
    source: SiteRedirectSourcePath;
    destination: SiteRedirectDestination;
};

export function useSiteRedirectForm(
    props?: UseFormProps<SiteRedirectFormInputs, never, SiteRedirectFormOutputs>
) {
    return useForm<SiteRedirectFormInputs, never, SiteRedirectFormOutputs>({
        defaultValues: {
            source: '',
            destination: null,
        },
        ...props,
    });
}
const SiteRedirectSourcePathRegex = new RegExp(SITE_REDIRECT_SOURCE_PATH_PATTERN);

function checkIsValidSiteRedirectSourcePath(value: string): value is SiteRedirectSourcePath {
    return SiteRedirectSourcePathRegex.test(value);
}

function validateSiteRedirectSourcePath(value: string) {
    if (checkIsValidSiteRedirectSourcePath(value)) {
        return true;
    }
    return 'Must be a valid root path (e.g. /some-page)';
}

export function SiteRedirectFieldset() {
    const { site, organizationId } = useSiteContext();
    return (
        <Fieldset>
            <TextField<SiteRedirectFormInputs>
                name="source"
                validate={validateSiteRedirectSourcePath}
                required
            >
                <Label>Source path</Label>
                <Description>URL path users will visit (e.g., /old-page).</Description>
                <TextInput autoFocus placeholder="/old-page" />
                <FieldError />
            </TextField>
            <SiteRedirectDestinationPickerField organizationId={organizationId} siteId={site.id} />
        </Fieldset>
    );
}

function SiteRedirectDestinationPickerField(
    props: Omit<SiteRedirectDestinationPickerProps, 'value' | 'onChange'>
) {
    const form = useFormContext<SiteRedirectFormInputs>();
    const controller = useController({
        name: 'destination',
        control: form.control,
        rules: {
            required: 'Required',
        },
    });

    return (
        <FieldControllerProvider controller={controller}>
            <FieldGroup>
                <Label>Destination content</Label>
                <Description>Select the page or content users will be redirected to.</Description>
                <div className="flex">
                    <SiteRedirectDestinationPicker
                        {...props}
                        ref={controller.field.ref}
                        value={controller.field.value}
                        onChange={controller.field.onChange}
                        onBlur={controller.field.onBlur}
                        disabled={controller.field.disabled}
                    />
                </div>
                <FieldError />
            </FieldGroup>
        </FieldControllerProvider>
    );
}
